import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';

import { LoanPayoffLineChart } from '../../../components/charts/LoanPayoffLineChart';
import { AmortizationTable, LoanSummaryCard } from '../../../components/loan';
import { WidgetInsightsSlot } from '../../../components/assistant/WidgetInsightCallout';
import { PageHeader } from '../../../components/layout/PageHeader';
import { WebScreenBody } from '../../../components/layout/WebScreenBody';
import { WebWidgetCell, WebWidgetGrid } from '../../../components/layout/WebWidgetGrid';
import { GlassButton } from '../../../components/ui/GlassButton';
import { WidgetRefreshScope } from '../../../contexts/WidgetRefreshContext';
import { GlassCard } from '../../../components/ui/GlassCard';
import { SampleDataBanner } from '../../../components/ui/SampleDataBanner';
import { useAuth } from '../../../contexts/AuthContext';
import { useSampleData } from '../../../contexts/SampleDataContext';
import { navigateToHome, navigateToWealth } from '../../../core/navigation/mainTabLinks';
import type { MainTabParamList } from '../../../core/navigation/types';
import { colors, spacing, typography } from '../../../core/theme/tokens';
import { useAppRefresh } from '../../../hooks/useAppRefresh';
import { useHomeLoanPlanner } from '../../../hooks/useHomeLoanPlanner';
import { formatLoanDuration } from '../../../utils/loanAmortization';

export function LoanTabScreen() {
  const navigation = useNavigation<BottomTabNavigationProp<MainTabParamList>>();
  const { isSampleMode } = useSampleData();
  const { isAuthenticated } = useAuth();
  const planner = useHomeLoanPlanner();
  const { refreshAll, refreshing } = useAppRefresh();
  const pageRefresh = () => {
    void refreshAll('loan_page');
  };
  const showDebtInsight = isAuthenticated && !isSampleMode;

  return (
    <WebScreenBody>
      {isSampleMode && <SampleDataBanner />}

      <PageHeader
        title="Home loan"
        subtitle={
          planner.hasMortgageSeed
            ? 'Amortization from your onboarding loan — tweak inputs or add extras'
            : 'Model payoff with rate, term, and optional extra payments'
        }
        onRefresh={pageRefresh}
        refreshing={refreshing}
      >
        <View style={styles.linkRow}>
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={() => navigateToHome(navigation)}
            accessibilityLabel="Open home overview"
          >
            Home
          </GlassButton>
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={() => navigateToWealth(navigation)}
            accessibilityLabel="Open wealth"
          >
            Wealth
          </GlassButton>
        </View>
      </PageHeader>

      <WidgetRefreshScope refresh={pageRefresh} refreshing={refreshing}>
      <WebWidgetGrid>
        <WebWidgetCell span="full">
          <LoanSummaryCard
            label={planner.label}
            principal={planner.principal}
            interestRatePct={planner.interestRatePct}
            termYears={planner.termYears}
            monthlyPayment={planner.monthlyPayment}
            monthsToPayoff={planner.accelerated.monthsToPayoff}
            cost={planner.acceleratedCost}
            baselineCost={planner.baselineCost}
            monthsSaved={planner.monthsSaved}
            interestSaved={planner.interestSaved}
            hasAnyExtra={planner.hasAnyExtra}
            startMonth={planner.startMonth}
            paidToDate={planner.paidToDate}
            onPrincipalChange={planner.setPrincipal}
            onRateChange={planner.setInterestRatePct}
            onTermChange={planner.setTermYears}
            onStartMonthChange={planner.setStartMonth}
          />
          {showDebtInsight ? (
            <WidgetInsightsSlot widget="debt_overview" style={styles.widgetInsight} />
          ) : null}
        </WebWidgetCell>

        {planner.baselineSeries.length > 1 && (
          <WebWidgetCell span="full">
            <GlassCard>
              <Text style={styles.sectionTitle}>Payoff timeline</Text>
              <Text style={styles.sectionHint}>
                {planner.hasAnyExtra && planner.monthsSaved > 0
                  ? `Extras cut ${formatLoanDuration(planner.monthsSaved)} off the loan`
                  : 'Remaining balance by year — tap a dot for details'}
              </Text>
              <LoanPayoffLineChart
                baseline={planner.baselineSeries}
                accelerated={planner.acceleratedSeries}
                baselineRows={planner.baseline.rows}
                acceleratedRows={planner.accelerated.rows}
                showComparison={planner.hasAnyExtra}
                startMonth={planner.startMonth}
                height={220}
              />
            </GlassCard>
          </WebWidgetCell>
        )}

        <WebWidgetCell span="full">
          <GlassCard>
            <Text style={styles.sectionTitle}>Amortization schedule</Text>
            <Text style={styles.sectionHint}>
              Type an extra in any row — or use chips / every-month add-on above the table
            </Text>
            <AmortizationTable
              rows={planner.accelerated.rows}
              extras={planner.extras}
              recurringExtra={planner.recurringExtra}
              startMonth={planner.startMonth}
              currentMonth={
                planner.paidToDate?.status === 'in_progress'
                  ? planner.paidToDate.currentMonth
                  : null
              }
              onRecurringExtraChange={planner.setRecurringExtra}
              onSetExtraForMonth={planner.setExtraForMonth}
              onAddExtraPayment={planner.addExtraPayment}
              initialVisible={36}
            />
          </GlassCard>
        </WebWidgetCell>
      </WebWidgetGrid>
      </WidgetRefreshScope>
    </WebScreenBody>
  );
}

const styles = StyleSheet.create({
  pageHeader: {
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  pageTitle: {
    ...typography.title,
    fontSize: 26,
    color: colors.text,
  },
  pageSub: {
    ...typography.body,
    color: colors.textMuted,
  },
  linkRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  sectionTitle: {
    ...typography.headline,
    color: colors.text,
    fontSize: 18,
    marginBottom: spacing.xs,
  },
  sectionHint: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },
  widgetInsight: {
    marginTop: spacing.sm,
  },
});
